import React, { useMemo } from "react";
import {
  Mail,
  Phone,

  ArrowLeft,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";


const toSlug = (str = "") =>
  str
    .toString()
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
const ChannelPartnerDetail = () => {
  const [searchParams] = useSearchParams();

  const { module, slug } = useParams(); // ✅ module + slug
  const navigate = useNavigate();

  const {
    data: listRes,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["list_sevak", module],
    queryFn: async () => {
      const res = await axios.get(
        `https://virtualsevak.com/api/v1/public/${module}`,
      );
      return res.data; // { success, data: [...] }
    },
    enabled: !!module,
    staleTime: 1000 * 60 * 30,
  });
  const listArr = useMemo(() => {
    return Array.isArray(listRes?.data) ? listRes.data : [];
  }, [listRes]);

  const item = useMemo(() => {
    if (!slug) return null;
    const idFromSlug = slug.split("-").pop();
    return listArr.find((x) => String(x.id) === String(idFromSlug)) || null;
  }, [listArr, slug]);

  const currentIndex = useMemo(() => {
    if (!slug) return -1;
    const idFromSlug = slug.split("-").pop();
    return listArr.findIndex((x) => String(x.id) === String(idFromSlug));
  }, [listArr, slug]);

  const prevItem = useMemo(() => {
    if (currentIndex <= 0) return null;
    return listArr[currentIndex - 1] || null;
  }, [listArr, currentIndex]);

  const nextItem = useMemo(() => {
    if (currentIndex < 0 || currentIndex >= listArr.length - 1) return null;
    return listArr[currentIndex + 1] || null;
  }, [listArr, currentIndex]);
  const toRoute = (x) =>
    `/virtual-sevak/${module}/${toSlug(x?.site_name || x?.name || "item")}-${x?.id}`;

  if (isPending) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-gray-600">
        Loading...
      </div>
    );
  }

  if (isError || !listRes?.success) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-gray-600">
        Something went wrong.
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-3xl px-4 py-10">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-full border border-orange-100 bg-white px-3 py-2 text-sm font-semibold text-orange-900"
          >
            <ArrowLeft size={16} /> Back
          </button>

          <div className="mt-6 rounded-2xl border border-orange-100 bg-orange-50 p-5">
            <h1 className="text-lg font-extrabold text-orange-950">
              Not Found
            </h1>
            <p className="mt-1 text-sm text-gray-700">
              No record found for slug:{" "}
              <span className="font-semibold">{slug}</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-orange-50/60 via-white to-white">
        <div className="mx-auto  max-w-[1380px] px-2 sm:px-4 py-4">
          <div className="px-4 py-2 flex flex-wrap items-center gap-3 sm:flex-nowrap justify-between">
            <button
              onClick={() => navigate(`/virtual-sevak/${module}`)}
              className="order-1 sm:order-none group inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900"
            >
              <ArrowLeft
                size={16}
                className="transition-transform group-hover:-translate-x-1"
              />
              Back to list
            </button>

            <div className="order-3 w-full sm:order-none sm:w-auto flex justify-center gap-3">
              <button
                disabled={!prevItem}
                title={
                  prevItem
                    ? prevItem?.site_name || prevItem?.name
                    : "No previous"
                }
                onClick={() => prevItem && navigate(toRoute(prevItem))}
                className="group flex items-center gap-2 px-4 py-2 rounded-xl
        border border-gray-200 bg-white
        hover:border-orange-400 hover:bg-orange-50
        disabled:opacity-40"
              >
                <ChevronLeft
                  size={16}
                  className="transition-transform group-hover:-translate-x-1"
                />
                <span className="text-sm font-semibold">Prev</span>
              </button>

              <button
                disabled={!nextItem}
                onClick={() => nextItem && navigate(toRoute(nextItem))}
                className="group flex items-center gap-2 px-4 py-2 rounded-xl
        bg-orange-500 text-white
        hover:bg-orange-600
        disabled:opacity-40"
                title={
                  nextItem ? nextItem?.site_name || nextItem?.name : "No next"
                }
              >
                <span className="text-sm font-semibold">Next</span>
                <ChevronRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </button>
            </div>

            {item?.subdomain && (
              <a
                href={item.subdomain}
                target="_blank"
                rel="noopener noreferrer"
                className="
        order-2 sm:order-none
        inline-flex items-center gap-1.5
        px-3 py-1.5 rounded-md
        bg-gray-100 text-gray-700
        font-medium text-xs sm:text-sm
        hover:bg-gray-200 transition-colors
      "
              >
                Visit on Site <span>↗</span>
              </a>
            )}
          </div>
          <section className="mt-1 overflow-hidden rounded-xl">
            {/* Banner */}
            <div className="bg-[#E07436] px-4 py-4">
              <div className="grid grid-cols-12 items-start gap-4">
                {/* Org Logo */}
                <div className="col-span-12 sm:col-span-4 flex justify-center sm:justify-start">
                  {item?.org_logo && (
                    <img
                      src={item.org_logo}
                      alt="org logo"
                      className="h-16 w-auto sm:h-24 sm:w-40 object-contain sm:object-fill"
                    />
                  )}
                </div>

                {/* Profile + Title */}
                <div className="col-span-12 sm:col-span-6 flex items-center gap-4">
                  {item?.picture && (
                    <img
                      src={item.picture}
                      alt={item?.department_name}
                      className="h-16 w-[100px] sm:h-24 sm:w-40 object-fill"
                    />
                  )}

                  <div>
                    {/* Title + Labels */}
                    <div className="flex flex-wrap items-center gap-3">
                      <h1 className="text-xl sm:text-2xl font-bold text-white capitalize">
                        {item?.department_name}
                      </h1>
                    </div>

                    {/* Description */}
                    {!!item?.description && (
                      <p
                        className="text-xs sm:text-sm text-white/90 line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: item?.description }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="relative h-[200px] sm:h-[500px] bg-orange-50">
              <img
                src={item?.banner_image}
                alt="banner"
                className="h-full w-full object-fill"
                loading="lazy"
              />
            </div>

            

            {/* Sevaks */}
            <div className=" mt-3">
              <h2 className="text-xl font-semibold text-orange-700 mb-2">Sevaks</h2>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {item?.sevaks?.map((s, i) => (
                  <div
                    key={i}
                    className="group relative border border-gray-100 rounded-2xl p-5 bg-white shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                    onClick={()=>{
                      const qs = searchParams.toString(); // keep country/state/city...;

                      navigate(
                        `/virtual-sevak/sevaks/${toSlug(s?.name)}-${s?.id}${qs ? `?${qs}` : ""}`,
                      );
                    }}
                  >
                    {/* Top */}
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      {s?.picture?.trim() ? (
                        <img
                          src={s.picture}
                          alt={s?.name}
                          className="w-14 h-14 rounded-full object-cover ring-2 ring-orange-100"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-semibold text-lg ring-2 ring-orange-50 capitalize">
                          {s?.name?.charAt(0) || "U"}
                        </div>
                      )}

                      {/* Name */}
                      <div>
                        <h3 className="font-semibold text-[15px] text-gray-900 capitalize">
                          {s?.name}
                        </h3>

                        <p className="text-[12px] text-orange-600 font-medium">
                          Sevak
                        </p>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t my-3"></div>

                    {/* Contact */}
                    <div className="text-[13px] space-y-2 text-gray-600">
                      {s?.email && (
                        <div className="flex items-center gap-2">
                          <Mail size={14} className="text-orange-500" />
                          <span className="truncate">{s.email}</span>
                        </div>
                      )}

                      {s?.telephone && (
                        <div className="flex items-center gap-2">
                          <Phone size={14} className="text-orange-500" />
                          <span>{s.telephone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
      <footer className="bg-[#1C2836] py-[20px] text-white">
        <div className="max-w-[1280px] mx-auto px-4">
          {/* Top Footer */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 p-2 bg-[#E07436]">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <img src={item?.org_logo} alt="logo" className="h-10 w-auto" />
            </div>

            {/* Nav Links */}
            <div className="flex flex-wrap items-center justify-center gap-5 text-[14px] font-medium">
              {item?.pages?.map((item, i) => (
                <a
                  key={i}
                  href={item?.url}
                  className="hover:text-orange-200 transition"
                  target="_blank"
                  rel="noreferrer"
                >
                  {item?.page_name}
                </a>
              ))}
            </div>
            <div></div>
          </div>

          {/* Bottom Footer */}
          <div className="flex items-center justify-center flex-col sm:flex-row gap-4 mt-[10px] pt-3 border-t border-white/30 text-[13px]">
            {item?.copyright_text && (
              <>
                <span>{item?.copyright_text}</span>
                <span className="hidden sm:inline-block">||</span>
              </>
            )}
            <span>Powered by virtualsevak.com</span>
          </div>
        </div>
      </footer>
    </>
  );
};

export default ChannelPartnerDetail;
